import React from 'react';
import { AppBar, Toolbar, Typography, Button, IconButton, Menu, MenuItem, Avatar, Box, Drawer, List, ListItem, ListItemText, ListItemButton, Badge } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import SearchBox from './SearchBox';
import { apiClient } from '../utils/api';

const Header: React.FC = () => {
   const { user, logout, token, identity } = useAuth();
   const navigate = useNavigate();
   const location = useLocation();
   const { t, i18n } = useTranslation();
   const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
   const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
   const [receivedTicketCount, setReceivedTicketCount] = React.useState(0);


  // Helper function to check if a path is active
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    void logout();
    handleClose();
    navigate('/');
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
  };

  const handleSearch = (term: string) => {
    // Navigate to home page with search term
    navigate(`/?search=${encodeURIComponent(term)}`);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  React.useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    const fetchReceivedTickets = async () => {
      if (!user || !token) {
        setReceivedTicketCount(0);
        return;
      }
      try {
        const response = await apiClient.get<{ tickets?: Array<{ initiatorRole?: string }> }>(
          '/api/tickets/user/tickets'
        );
        const tickets = response.data?.tickets || [];
        const incomingCount = tickets.filter((ticket: { initiatorRole?: string }) => ticket.initiatorRole === 'admin').length;
        setReceivedTicketCount(incomingCount);
      } catch (_error) {
        // Keep header resilient on API failures.
      }
    };

    fetchReceivedTickets();
    if (user && token) {
      timer = setInterval(fetchReceivedTickets, 60000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [user, token]);

  return (
    <AppBar
      position="static"
      color="primary"
      elevation={1}
      sx={{
        backgroundColor: 'primary.main',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ minHeight: 64 }}>
        {/* Logo/Brand */}
        <Box
          component={Link}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            mr: 2,
            '&:hover': {
              opacity: 0.8,
            },
          }}
        >
          <Box
            component="img"
            src="/logo.png"
            alt="Opinion Logo"
            sx={{
              height: 40,
              width: 'auto',
              mr: 2,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 700,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {t('common.siteName')}
          </Typography>
        </Box>

        {/* Navigation Links */}
        <Box sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' }, mr: 2 }}>
          <Button
            color="inherit"
            component={Link}
            to="/"
            sx={{
              mx: 1,
              backgroundColor: isActive('/') ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              borderRadius: 2,
            }}
          >
            {t('navigation.home')}
          </Button>
          <Button
            color="inherit"
            component={Link}
            to="/contact"
            sx={{
              mx: 1,
              backgroundColor: isActive('/contact') ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              borderRadius: 2,
            }}
          >
            {t('navigation.contact')}
          </Button>
          <Button
            color="inherit"
            component={Link}
            to="/how-to-use"
            sx={{
              mx: 1,
              backgroundColor: isActive('/how-to-use') ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              borderRadius: 2,
            }}
          >
            {t('navigation.howToUse')}
          </Button>
          <Button
            color="inherit"
            component={Link}
            to="/terms"
            sx={{
              mx: 1,
              backgroundColor: isActive('/terms') ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              borderRadius: 2,
            }}
          >
            {t('navigation.terms')}
          </Button>
          <Button
            color="inherit"
            component={Link}
            to="/disclaimer"
            sx={{
              mx: 1,
              backgroundColor: isActive('/disclaimer') ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              borderRadius: 2,
            }}
          >
            {t('navigation.disclaimer')}
          </Button>
          <Button
            color="inherit"
            component={Link}
            to="/create-opinion"
            sx={{
              mx: 1,
              backgroundColor: isActive('/create-opinion') ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              borderRadius: 2,
            }}
          >
            {t('navigation.create')}
          </Button>
          {user && user.isAdmin && (
            <Button
              color="inherit"
              component={Link}
              to="/admin/tickets"
              sx={{
                mx: 1,
                backgroundColor: isActive('/admin/tickets') ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                borderRadius: 2,
              }}
            >
              {t('navigation.admin')}
            </Button>
          )}
          {identity.isOrgUser && (
            <Button
              color="inherit"
              component={Link}
              to="/org-admin"
              sx={{
                mx: 1,
                backgroundColor: isActive('/org-admin') ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                borderRadius: 2,
              }}
            >
              {t('navigation.orgAdmin')}
            </Button>
          )}
        </Box>

        {/* Search Box - Only show on non-home pages */}
        {location.pathname !== '/' && (
          <Box sx={{ flexGrow: 1, mx: 1, maxWidth: 500, display: { xs: 'none', sm: 'block' } }}>
            <SearchBox onSearch={handleSearch} />
          </Box>
        )}

        {/* Mobile Menu Button */}
        <IconButton
          color="inherit"
          sx={{ display: { xs: 'block', lg: 'none' }, mr: 1 }}
          onClick={handleMobileMenuToggle}
        >
          <MenuIcon />
        </IconButton>

        {/* Right side items */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>

          {/* Language Switcher */}
          <Box sx={{ mr: 1 }}>
            <Button
              color="inherit"
              size="small"
              onClick={() => handleLanguageChange('en')}
              sx={{
                minWidth: 'auto',
                px: 0.5,
                fontSize: '0.8rem',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              EN
            </Button>
            <Typography component="span" sx={{ color: 'white', mx: 0.25 }}>
              |
            </Typography>
            <Button
              color="inherit"
              size="small"
              onClick={() => handleLanguageChange('fa')}
              sx={{
                minWidth: 'auto',
                px: 0.5,
                fontSize: '0.8rem',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              FA
            </Button>
          </Box>

          {user ? (
            <>
              <Badge
                color="error"
                variant={receivedTicketCount > 0 ? 'dot' : 'standard'}
                invisible={receivedTicketCount <= 0}
                overlap="circular"
              >
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                  sx={{
                    ml: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <Avatar
                    src={user.avatar}
                    alt={user.username || user.name || user.email}
                    sx={{ width: 28, height: 28 }}
                  >
                    {(user.username || user.name || user.email || 'User').charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Badge>

              {/* User Menu */}
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 150,
                  },
                }}
              >
                <MenuItem onClick={handleProfile} sx={{ py: 1.5 }}>
                  <Typography variant="body2">{t('common.profile')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => { handleClose(); navigate('/my-tickets'); }} sx={{ py: 1.5 }}>
                  <Typography variant="body2">
                    {t('contact.myTickets')}{receivedTicketCount > 0 ? ` (${receivedTicketCount})` : ''}
                  </Typography>
                </MenuItem>
                {user && user.isAdmin && (
                  <>
                    <MenuItem onClick={() => { handleClose(); navigate('/admin/tickets'); }} sx={{ py: 1.5 }}>
                      <Typography variant="body2">{t('navigation.adminTickets')}</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); navigate('/admin/dashboard'); }} sx={{ py: 1.5 }}>
                      <Typography variant="body2">{t('navigation.adminPanel')}</Typography>
                    </MenuItem>
                  </>
                )}
                <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                  <Typography variant="body2">{t('common.logout')}</Typography>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                color="inherit"
                component={Link}
                to="/login"
                size="small"
                sx={{
                  px: 1,
                  fontSize: '0.8rem',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                {t('common.login')}
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to="/register"
                size="small"
                sx={{
                  px: 1,
                  fontSize: '0.8rem',
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: 'white',
                  },
                }}
              >
                {t('common.register')}
              </Button>
            </Box>
          )}
        </Box>

        {/* Mobile Menu Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={handleMobileMenuClose}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              backgroundColor: 'primary.main',
              color: 'white',
            },
          }}
        >
          <List sx={{ pt: 8 }}>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/" onClick={handleMobileMenuClose}>
                <ListItemText primary={t('navigation.home')} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/contact" onClick={handleMobileMenuClose}>
                <ListItemText primary={t('navigation.contact')} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/how-to-use" onClick={handleMobileMenuClose}>
                <ListItemText primary={t('navigation.howToUse')} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/terms" onClick={handleMobileMenuClose}>
                <ListItemText primary={t('navigation.terms')} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/disclaimer" onClick={handleMobileMenuClose}>
                <ListItemText primary={t('navigation.disclaimer')} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/create-opinion" onClick={handleMobileMenuClose}>
                <ListItemText primary={t('navigation.create')} />
              </ListItemButton>
            </ListItem>
            {user && (
              <>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/profile" onClick={handleMobileMenuClose}>
                    <ListItemText primary={t('common.profile')} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/my-tickets" onClick={handleMobileMenuClose}>
                    <ListItemText primary={`${t('contact.myTickets')}${receivedTicketCount > 0 ? ` (${receivedTicketCount})` : ''}`} />
                  </ListItemButton>
                </ListItem>
                {user.isAdmin && (
                  <>
                    <ListItem disablePadding>
                      <ListItemButton component={Link} to="/admin/tickets" onClick={handleMobileMenuClose}>
                        <ListItemText primary={t('navigation.adminTickets')} />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemButton component={Link} to="/admin/dashboard" onClick={handleMobileMenuClose}>
                        <ListItemText primary={t('navigation.adminPanel')} />
                      </ListItemButton>
                    </ListItem>
                  </>
                )}
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { handleMobileMenuClose(); void logout(); navigate('/'); }}>
                    <ListItemText primary={t('common.logout')} />
                  </ListItemButton>
                </ListItem>
              </>
            )}
            {!user && (
              <>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/login" onClick={handleMobileMenuClose}>
                    <ListItemText primary={t('common.login')} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/register" onClick={handleMobileMenuClose}>
                    <ListItemText primary={t('common.register')} />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </List>
        </Drawer>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
